import { useState, useEffect } from 'react';

interface ConfettiPiece {
  id: number;
  x: number;
  y: number;
  rotation: number;
  color: string;
  size: number;
  delay: number;
  duration: number;
}

interface FreeGiftModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function FreeGiftModal({ isOpen, onClose }: FreeGiftModalProps) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [confetti, setConfetti] = useState<ConfettiPiece[]>([]);
  const [hasClaimed, setHasClaimed] = useState(false);
  const [claimedName, setClaimedName] = useState('');

  useEffect(() => {
    if (status === 'success') {
      const colors = ['#ff6b6b', '#ffd93d', '#6bcb77', '#ff9f43', '#ee5a6f', '#a8e6cf', '#ff7a59', '#feca57'];
      const pieces = Array.from({ length: 30 }, (_, i) => ({
        id: i,
        x: (Math.random() - 0.5) * 260,
        y: Math.random() * -200 - 40,
        rotation: Math.random() * 720,
        color: colors[Math.floor(Math.random() * colors.length)],
        size: Math.random() * 6 + 4,
        delay: Math.random() * 0.3,
        duration: Math.random() * 0.6 + 1.2,
      }));
      setConfetti(pieces);
      const timer = setTimeout(() => setConfetti([]), 2500);
      return () => clearTimeout(timer);
    }
  }, [status]);

  useEffect(() => {
    if (isOpen) {
      const storedClaimed = localStorage.getItem('gracie-lily-claimed');
      const storedName = localStorage.getItem('gracie-lily-name');
      if (storedClaimed === 'true' && storedName) {
        setHasClaimed(true);
        setClaimedName(storedName);
      } else {
        setHasClaimed(false);
        setClaimedName('');
      }
      setStatus('idle');
      setName('');
      setEmail('');
      setErrorMsg('');
    }
  }, [isOpen]);

  const handleResetClaim = () => {
    localStorage.removeItem('gracie-lily-claimed');
    localStorage.removeItem('gracie-lily-name');
    setHasClaimed(false);
    setClaimedName('');
    setStatus('idle');
  };

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMsg('');

    if (!name.trim() || !email.trim()) {
      setStatus('error');
      setErrorMsg('Please fill in all fields.');
      return;
    }

    if (name.trim().length < 2) {
      setStatus('error');
      setErrorMsg('Please enter your full name.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setStatus('error');
      setErrorMsg('Please enter a valid email address.');
      return;
    }

    try {
      const response = await fetch('https://formspree.io/f/mgobgavv', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
        }),
      });

      if (response.ok) {
        setStatus('success');
        setName('');
        setEmail('');
        localStorage.setItem('gracie-lily-claimed', 'true');
        localStorage.setItem('gracie-lily-name', name.trim());
      } else {
        const data = await response.json().catch(() => null);
        setStatus('error');
        setErrorMsg(
          data?.error || 'Something went wrong. Please try again.'
        );
      }
    } catch {
      setStatus('error');
      setErrorMsg('Something went wrong. Please try again.');
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="bg-white rounded-3xl max-w-md w-full p-6 md:p-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-foreground-700 hover:text-primary-500 cursor-pointer"
          aria-label="Close modal"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        <div className="flex flex-col items-center text-center">
          <div className="text-4xl mb-3">🎁</div>
          <h3 className="text-2xl md:text-3xl font-black text-primary-500 mb-2">
            Claim Your Free Gift!
          </h3>
          <p className="text-foreground-700 text-sm md:text-base mb-6 leading-relaxed">
            Enter your name and email to get instant access to the FREE Gracie &amp; Lily Coloring Book!
          </p>
        </div>

        {hasClaimed && status !== 'success' ? (
          <div className="flex flex-col items-center gap-4 py-2">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-100">
              <i className="ri-user-heart-line text-primary-500 text-2xl"></i>
            </div>
            <h4 className="text-xl font-bold text-primary-500">Welcome back, {claimedName}!</h4>
            <p className="text-foreground-700 text-sm text-center leading-relaxed">
              You've already claimed your free Gracie &amp; Lily Coloring Book. Grab it again below!
            </p>

            <a
              href="https://drive.google.com/uc?export=download&id=15xTbZDySM_f-mBKPu_HAn_dUD-WZanJl"
              target="_blank"
              rel="noopener noreferrer"
              className="relative w-full max-w-[220px] aspect-[4/3] rounded-xl overflow-hidden ring-4 ring-primary-100 shadow-2xl cursor-pointer hover:scale-105 hover:brightness-110 transition-all duration-300 block"
              aria-label="Download Gracie & Lily Coloring Book"
            >
              <img
                src="https://static.readdy.ai/image/ff63a95a3d068db59710e28833339873/1a089ad3da978625cc2422a24763774b.png"
                alt="Gracie & Lily Coloring Book Cover"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-colors flex items-center justify-center">
                <div className="w-10 h-10 bg-white/90 rounded-full flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity shadow-lg">
                  <i className="ri-download-line text-primary-500 text-lg"></i>
                </div>
              </div>
            </a>

            <a
              href="https://drive.google.com/uc?export=download&id=15xTbZDySM_f-mBKPu_HAn_dUD-WZanJl"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-primary-500 text-white px-6 py-3 rounded-lg font-bold text-sm whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors flex items-center gap-2 no-underline"
            >
              <i className="ri-download-line text-base"></i>
              Download Again
            </a>

            <button
              onClick={handleResetClaim}
              className="text-foreground-600 text-xs underline cursor-pointer hover:text-primary-500 transition-colors"
            >
              Not you? Claim with a different email
            </button>
          </div>
        ) : status === 'success' ? (
          <div className="relative flex flex-col items-center gap-4 py-2">
            <style>{`
              @keyframes confettiBurst {
                0% { transform: translate(0, 0) rotate(0deg); opacity: 1; }
                100% { transform: translate(var(--tx), var(--ty)) rotate(var(--tr)); opacity: 0; }
              }
            `}</style>
            {confetti.map((piece) => (
              <div
                key={piece.id}
                className="absolute z-10 rounded-sm pointer-events-none"
                style={{
                  left: '50%',
                  top: '35%',
                  width: `${piece.size}px`,
                  height: `${piece.size}px`,
                  backgroundColor: piece.color,
                  '--tx': `${piece.x}px`,
                  '--ty': `${piece.y}px`,
                  '--tr': `${piece.rotation}deg`,
                  animation: `confettiBurst ${piece.duration}s ease-out forwards`,
                  animationDelay: `${piece.delay}s`,
                } as React.CSSProperties}
              />
            ))}
            <a
              href="https://drive.google.com/uc?export=download&id=15xTbZDySM_f-mBKPu_HAn_dUD-WZanJl"
              target="_blank"
              rel="noopener noreferrer"
              className="relative w-full max-w-[220px] aspect-[4/3] rounded-xl overflow-hidden ring-4 ring-primary-100 shadow-2xl cursor-pointer hover:scale-105 hover:brightness-110 transition-all duration-300 block"
              aria-label="Download Gracie & Lily Coloring Book"
            >
              <img
                src="https://static.readdy.ai/image/ff63a95a3d068db59710e28833339873/1a089ad3da978625cc2422a24763774b.png"
                alt="Gracie & Lily Coloring Book Cover"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-colors flex items-center justify-center">
                <div className="w-10 h-10 bg-white/90 rounded-full flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity shadow-lg">
                  <i className="ri-download-line text-primary-500 text-lg"></i>
                </div>
              </div>
            </a>
            <p className="text-xs text-foreground-600 font-semibold uppercase tracking-wider">
              Your Free Download
            </p>
            <div className="text-5xl">🎉</div>
            <h4 className="text-xl font-bold text-primary-500">You're All Set!</h4>
            <p className="text-foreground-700 text-sm text-center">
              Check your inbox for your free coloring book. Happy coloring!
            </p>
            <a
              href="https://drive.google.com/uc?export=download&id=15xTbZDySM_f-mBKPu_HAn_dUD-WZanJl"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-primary-500 text-white px-6 py-3 rounded-lg font-bold text-sm whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors flex items-center gap-2 no-underline"
            >
              <i className="ri-download-line text-base"></i>
              Download Coloring Book
            </a>
            <button
              onClick={onClose}
              className="bg-accent-500 text-white px-6 py-3 rounded-lg font-bold text-sm whitespace-nowrap cursor-pointer hover:bg-accent-600 transition-colors"
            >
              Close
            </button>
          </div>
        ) : (
          <form
            id="free-coloring-book-form"
            onSubmit={handleSubmit}
            className="flex flex-col gap-4"
          >
            <div className="flex flex-col gap-1.5">
              <label htmlFor="free-gift-name" className="text-foreground-950 text-sm font-semibold">
                Your Name
              </label>
              <input
                id="free-gift-name"
                type="text"
                name="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Sarah"
                className="w-full px-4 py-3 rounded-lg border-2 border-background-200 focus:border-primary-500 focus:outline-none text-sm"
                maxLength={100}
                required
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="free-gift-email" className="text-foreground-950 text-sm font-semibold">
                Email Address
              </label>
              <input
                id="free-gift-email"
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="e.g. sarah@email.com"
                className="w-full px-4 py-3 rounded-lg border-2 border-background-200 focus:border-primary-500 focus:outline-none text-sm"
                maxLength={100}
                required
              />
            </div>

            {status === 'error' && (
              <p className="text-red-500 text-sm">{errorMsg}</p>
            )}

            <button
              type="submit"
              disabled={status === 'submitting'}
              className="bg-accent-500 text-white px-6 py-3.5 rounded-lg font-bold text-base whitespace-nowrap cursor-pointer hover:bg-accent-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {status === 'submitting' ? 'Sending...' : 'Get My Free Coloring Book! 🎨'}
            </button>

            <p className="text-foreground-600 text-xs text-center">
              No spam, ever. We respect your privacy.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}